import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Search, Plus } from "lucide-react";

export interface MergeField {
  tag: string;
  label: string;
  category: string;
  description: string;
  example?: string;
}

// Comprehensive merge fields from database schema
export const MERGE_FIELDS: MergeField[] = [
  // Contact/Personal Information
  { tag: "{{firstName}}", label: "First Name", category: "Contact", description: "Recipient's first name", example: "John" },
  { tag: "{{lastName}}", label: "Last Name", category: "Contact", description: "Recipient's last name", example: "Smith" },
  { tag: "{{email}}", label: "Email Address", category: "Contact", description: "Primary email address", example: "john.smith@company.com" },
  { tag: "{{phone}}", label: "Phone Number", category: "Contact", description: "Primary phone number", example: "(555) 123-4567" },
  { tag: "{{fullName}}", label: "Full Name", category: "Contact", description: "First and last name combined", example: "John Smith" },
  
  // Company/Organization Information
  { tag: "{{companyName}}", label: "Company Name", category: "Company", description: "Company or organization name", example: "ABC Corporation" },
  { tag: "{{primaryContactName}}", label: "Primary Contact", category: "Company", description: "Primary contact person at company", example: "Jane Doe" },
  { tag: "{{contactEmail}}", label: "Company Email", category: "Company", description: "Primary company email", example: "info@company.com" },
  { tag: "{{contactPhone}}", label: "Company Phone", category: "Company", description: "Primary company phone", example: "(555) 987-6543" },
  { tag: "{{website}}", label: "Company Website", category: "Company", description: "Company website URL", example: "www.company.com" },
  { tag: "{{employeeCount}}", label: "Employee Count", category: "Company", description: "Number of employees", example: "150" },
  { tag: "{{entityType}}", label: "Entity Type", category: "Company", description: "Business entity type", example: "LLC" },
  { tag: "{{ein}}", label: "EIN", category: "Company", description: "Employer Identification Number", example: "12-3456789" },
  { tag: "{{address}}", label: "Company Address", category: "Company", description: "Company address", example: "123 Main St, City, State 12345" },
  
  // Employee Information
  { tag: "{{employeeId}}", label: "Employee ID", category: "Employee", description: "Employee identification number", example: "EMP001" },
  { tag: "{{department}}", label: "Department", category: "Employee", description: "Employee department", example: "Marketing" },
  { tag: "{{position}}", label: "Position/Title", category: "Employee", description: "Job title or position", example: "Marketing Manager" },
  { tag: "{{hireDate}}", label: "Hire Date", category: "Employee", description: "Employee hire date", example: "January 15, 2023" },
  { tag: "{{employmentType}}", label: "Employment Type", category: "Employee", description: "Employment classification", example: "Full-Time" },
  { tag: "{{salary}}", label: "Salary", category: "Employee", description: "Employee salary", example: "$75,000" },
  { tag: "{{compensationType}}", label: "Compensation Type", category: "Employee", description: "Salary or hourly", example: "Salary" },
  { tag: "{{payCycle}}", label: "Pay Cycle", category: "Employee", description: "Payment frequency", example: "Bi-Weekly" },
  
  // Personal Details
  { tag: "{{dateOfBirth}}", label: "Date of Birth", category: "Personal", description: "Employee date of birth", example: "March 10, 1985" },
  { tag: "{{age}}", label: "Age", category: "Personal", description: "Employee age", example: "38" },
  { tag: "{{maritalStatus}}", label: "Marital Status", category: "Personal", description: "Marital status", example: "Married" },
  { tag: "{{sex}}", label: "Gender", category: "Personal", description: "Gender", example: "Female" },
  
  // Address Information
  { tag: "{{address1}}", label: "Address Line 1", category: "Address", description: "Primary address line", example: "456 Oak Street" },
  { tag: "{{address2}}", label: "Address Line 2", category: "Address", description: "Apartment, suite, unit", example: "Apt 2B" },
  { tag: "{{city}}", label: "City", category: "Address", description: "City", example: "Springfield" },
  { tag: "{{state}}", label: "State", category: "Address", description: "State or province", example: "Illinois" },
  { tag: "{{zipCode}}", label: "ZIP Code", category: "Address", description: "Postal code", example: "62701" },
  { tag: "{{county}}", label: "County", category: "Address", description: "County", example: "Sangamon County" },
  { tag: "{{fullAddress}}", label: "Full Address", category: "Address", description: "Complete address", example: "456 Oak St, Apt 2B, Springfield, IL 62701" },
  
  // Plan and Benefits Information
  { tag: "{{planName}}", label: "Plan Name", category: "Benefits", description: "Insurance plan name", example: "Premium Health Plus" },
  { tag: "{{planType}}", label: "Plan Type", category: "Benefits", description: "Type of insurance plan", example: "Health" },
  { tag: "{{carrier}}", label: "Insurance Carrier", category: "Benefits", description: "Insurance company", example: "Blue Cross Blue Shield" },
  { tag: "{{planCode}}", label: "Plan Code", category: "Benefits", description: "Plan identification code", example: "BCBS-PPO-001" },
  { tag: "{{networkType}}", label: "Network Type", category: "Benefits", description: "Network type", example: "PPO" },
  { tag: "{{metalTier}}", label: "Metal Tier", category: "Benefits", description: "ACA metal tier", example: "Gold" },
  { tag: "{{effectiveDate}}", label: "Effective Date", category: "Benefits", description: "Coverage start date", example: "January 1, 2024" },
  { tag: "{{endDate}}", label: "End Date", category: "Benefits", description: "Coverage end date", example: "December 31, 2024" },
  { tag: "{{renewalDate}}", label: "Renewal Date", category: "Benefits", description: "Plan renewal date", example: "January 1, 2025" },
  { tag: "{{coverageTier}}", label: "Coverage Tier", category: "Benefits", description: "Coverage level", example: "Employee + Family" },
  
  // Premium and Cost Information
  { tag: "{{totalPremium}}", label: "Total Premium", category: "Cost", description: "Total monthly premium", example: "$650.00" },
  { tag: "{{employeePremium}}", label: "Employee Premium", category: "Cost", description: "Employee portion of premium", example: "$125.00" },
  { tag: "{{employerPremium}}", label: "Employer Premium", category: "Cost", description: "Employer portion of premium", example: "$525.00" },
  { tag: "{{payrollDeduction}}", label: "Payroll Deduction", category: "Cost", description: "Amount deducted from payroll", example: "$125.00" },
  
  // Enrollment Information
  { tag: "{{enrollmentDate}}", label: "Enrollment Date", category: "Enrollment", description: "Date enrolled in plan", example: "December 15, 2023" },
  { tag: "{{enrollmentStatus}}", label: "Enrollment Status", category: "Enrollment", description: "Current enrollment status", example: "Active" },
  { tag: "{{enrollmentSource}}", label: "Enrollment Source", category: "Enrollment", description: "How enrollment occurred", example: "Open Enrollment" },
  
  // System Information
  { tag: "{{todayDate}}", label: "Today's Date", category: "System", description: "Current date", example: "August 29, 2025" },
  { tag: "{{currentYear}}", label: "Current Year", category: "System", description: "Current year", example: "2025" },
  { tag: "{{agentName}}", label: "Agent Name", category: "System", description: "Insurance agent name", example: "Michael Johnson" },
  { tag: "{{agencyName}}", label: "Agency Name", category: "System", description: "Insurance agency name", example: "Premier Insurance Group" },
  
  // Prospect Information
  { tag: "{{prospectName}}", label: "Prospect Name", category: "Prospect", description: "Prospect company name", example: "Future Client LLC" },
  { tag: "{{prospectStatus}}", label: "Prospect Status", category: "Prospect", description: "Lead status", example: "Qualified" },
  { tag: "{{prospectSource}}", label: "Prospect Source", category: "Prospect", description: "How prospect was acquired", example: "Referral" },
  { tag: "{{expectedValue}}", label: "Expected Value", category: "Prospect", description: "Expected deal value", example: "$25,000" },
  { tag: "{{expectedCloseDate}}", label: "Expected Close Date", category: "Prospect", description: "Expected close date", example: "November 15, 2025" },
  { tag: "{{lastContactDate}}", label: "Last Contact Date", category: "Prospect", description: "Last contact date", example: "August 20, 2025" },
];

const CATEGORIES = Array.from(new Set(MERGE_FIELDS.map(field => field.category)));

interface MergeFieldSelectorProps {
  onSelect: (tag: string) => void;
  buttonText?: string;
  buttonVariant?: "default" | "outline" | "ghost";
  buttonSize?: "default" | "sm" | "lg";
  buttonIcon?: React.ReactNode;
  buttonClassName?: string;
}

export function MergeFieldSelector({ 
  onSelect, 
  buttonText = "Insert Merge Field",
  buttonVariant = "outline",
  buttonSize = "sm",
  buttonIcon,
  buttonClassName
}: MergeFieldSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const filteredFields = MERGE_FIELDS.filter(field => {
    const matchesSearch = search === "" || 
      field.label.toLowerCase().includes(search.toLowerCase()) ||
      field.tag.toLowerCase().includes(search.toLowerCase()) ||
      field.description.toLowerCase().includes(search.toLowerCase());
    
    const matchesCategory = selectedCategory === null || field.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleSelect = (tag: string) => {
    onSelect(tag);
    setIsOpen(false);
    setSearch("");
    setSelectedCategory(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant={buttonVariant} 
          size={buttonSize} 
          className={`gap-2 ${buttonClassName || ''}`}
        >
          {buttonIcon || <Plus className="h-4 w-4" />}
          {buttonText}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Insert Merge Field</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search merge fields..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === null ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(null)}
            >
              All Categories
            </Button>
            {CATEGORIES.map(category => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
          
          {/* Merge Fields List */}
          <ScrollArea className="h-96 border rounded-md">
            <div className="p-4 space-y-2">
              {filteredFields.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  No merge fields found for your search.
                </div>
              ) : (
                filteredFields.map((field) => (
                  <div
                    key={field.tag}
                    className="flex items-center justify-between p-3 hover:bg-muted rounded-lg cursor-pointer border"
                    onClick={() => handleSelect(field.tag)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{field.label}</span>
                        <Badge variant="secondary" className="text-xs">
                          {field.category}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground mb-1">
                        {field.description}
                      </div>
                      <div className="flex items-center gap-4">
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {field.tag}
                        </code>
                        {field.example && (
                          <span className="text-xs text-muted-foreground">
                            Example: {field.example}
                          </span>
                        )}
                      </div>
                    </div>
                    <Plus className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
          
          <div className="text-sm text-muted-foreground">
            Click on any merge field to insert it at your cursor position.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}